/* import __COLOCATED_TEMPLATE__ from './schedule-picker.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import type CustomReportsService from 'embercom/services/custom-reports-service';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import moment from 'moment-timezone';

export type ScheduleSelection =
  | { kind: 'now' }
  | { kind: 'schedule'; scheduledDate: string; timezone: string };

interface Args {
  pendingUpdate?: ScheduledContentUpdate;
  canSchedule: boolean;
  nowLabel: string;
  scheduleLabel: string;
  scheduleDescription: string;
  pastDateError: string;
  onChange: (selection: ScheduleSelection) => void;
}

interface Signature {
  Args: Args;
}

enum WhenOption {
  Now = 'now',
  Schedule = 'schedule',
}

export default class SchedulePicker extends Component<Signature> {
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;

  @tracked whenOption: WhenOption = this.args.pendingUpdate ? WhenOption.Schedule : WhenOption.Now;
  @tracked selectedTimezone: string = this.defaultTimezone;
  @tracked scheduledDate: Date = this.initialScheduledDate;
  @tracked today = new Date();

  get isScheduleMode(): boolean {
    return this.whenOption === WhenOption.Schedule;
  }

  get defaultTimezone(): string {
    return this.args.pendingUpdate?.timezone || this.appService.app.timezone;
  }

  get initialScheduledDate(): Date {
    let pending = this.args.pendingUpdate;
    if (pending?.changeAt) {
      return moment.tz(pending.changeAt, this.defaultTimezone).toDate();
    }
    return moment
      .tz(this.appService.app.timezone)
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
  }

  get timezoneOptions(): { text: string; value: string }[] {
    return this.customReportsService
      .timezones()
      .map((tz: { description: string; value: string }) => ({
        text: tz.description,
        value: tz.value,
      }));
  }

  get selectedTimezoneDescription(): string {
    let tz = this.customReportsService
      .timezones()
      .find((t: { value: string; description: string }) => t.value === this.selectedTimezone);
    return tz?.description ?? this.selectedTimezone;
  }

  // The DateAndTimePicker disables past *dates* but not past hours within today,
  // so a same-day selection can still resolve to an instant in the past.
  get isScheduledInPast(): boolean {
    return this.scheduledDate.getTime() <= Date.now();
  }

  get showPastDateError(): boolean {
    return this.isScheduleMode && this.isScheduledInPast;
  }

  @action bubbleSelection() {
    if (this.isScheduleMode) {
      this.args.onChange({
        kind: 'schedule',
        scheduledDate: this.scheduledDate.toISOString(),
        timezone: this.selectedTimezone,
      });
    } else {
      this.args.onChange({ kind: 'now' });
    }
  }

  @action onTimezoneChange(value: string) {
    let wallClock = moment(this.scheduledDate)
      .tz(this.selectedTimezone)
      .format('YYYY-MM-DD HH:mm:ss');
    this.selectedTimezone = value;
    this.scheduledDate = moment.tz(wallClock, 'YYYY-MM-DD HH:mm:ss', value).toDate();
  }

  @action onScheduledDateChange(date: Date) {
    this.scheduledDate = date;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::SchedulePicker': typeof SchedulePicker;
    'knowledge-hub/content-editor/article-content/schedule-picker': typeof SchedulePicker;
  }
}
